// DropSessionReviewModal — May 7, 2026
//
// Pops at the end of a manager's drop session. Lists every pin dropped in
// the session with its auto-filled parcel intel (homeowner, year built,
// est. roof squares, last sale) + qualifies/preferred chip. Tap a row → the
// host map flies to that pin and opens its detail sheet.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { titleCaseOwner } from "./PinIntelSection";

interface SessionPin {
  id: string;
  lat: number;
  lon: number;
  property_street: string | null;
  property_city: string | null;
  homeowner_name: string | null;
  parcel_id: string | null;
  // joined parcel fields
  parcel?: {
    year_built: number | null;
    estimated_squares: number | null;
    last_sale_date: string | null;
    last_sale_price: number | null;
    qualifies: boolean | null;
    is_preferred: boolean | null;
  } | null;
}

type SortKey = "newest" | "preferred" | "squares" | "year" | "address";

export default function DropSessionReviewModal({
  sessionId, pinCount, savedNow, skipped, dedupeUnavailable, onClose, onJumpToPin, onPinDeleted,
}: {
  sessionId: string;
  pinCount: number;
  /** Pins saved by the End run that just finished (Sep 9, 2026 — explicit
   *  save confirmation so a slow End never reads as "didn't save"). */
  savedNow?: number;
  /** Drops skipped because the house already had a pin. */
  skipped?: number;
  /** True when the batched parcel lookup failed, so already-pinned houses
   *  couldn't be screened out this run. */
  dedupeUnavailable?: boolean;
  onClose: () => void;
  onJumpToPin: (pin: { id: string; lat: number; lon: number }) => void;
  /** Notify host so it can drop the pin from the live map state */
  onPinDeleted?: (pinId: string) => void;
}) {
  const [pins, setPins] = useState<SessionPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortKey>("preferred");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("knock_pins")
        .select("id, lat, lon, property_street, property_city, homeowner_name, parcel_id, parcel:parcels!knock_pins_parcel_id_fkey(year_built, estimated_squares, last_sale_date, last_sale_price, qualifies, is_preferred)")
        .eq("drop_session_id", sessionId)
        .order("dropped_at", { ascending: false });
      if (cancelled) return;
      setPins((data ?? []) as any as SessionPin[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  async function handleDelete(p: SessionPin, ev: React.MouseEvent) {
    ev.stopPropagation();
    const homeowner = titleCaseOwner(p.homeowner_name) ?? p.homeowner_name ?? "this pin";
    if (!window.confirm(`Delete pin for ${homeowner} at ${p.property_street ?? "?"}? This can\'t be undone.`)) return;
    setDeletingId(p.id);
    const { error } = await supabase.from("knock_pins").delete().eq("id", p.id);
    setDeletingId(null);
    if (error) { window.alert(`Could not delete pin: ${error.message}`); return; }
    setPins((prev) => prev.filter((x) => x.id !== p.id));
    onPinDeleted?.(p.id);
  }

  const visible = useMemo(() => {
    const cmp: Record<SortKey, (a: SessionPin, b: SessionPin) => number> = {
      newest:    () => 0, // already sorted newest-first by query
      preferred: (a, b) => {
        const score = (p: SessionPin) =>
          (p.parcel?.is_preferred ? 2 : 0) + (p.parcel?.qualifies ? 1 : 0);
        return score(b) - score(a);
      },
      squares:   (a, b) => (Number(b.parcel?.estimated_squares) || 0) - (Number(a.parcel?.estimated_squares) || 0),
      year:      (a, b) => (a.parcel?.year_built ?? 9999) - (b.parcel?.year_built ?? 9999),
      address:   (a, b) => (a.property_street ?? "").localeCompare(b.property_street ?? ""),
    };
    return [...pins].sort(cmp[sort]);
  }, [pins, sort]);

  // Aggregate stats
  const totals = useMemo(() => {
    const preferred = pins.filter((p) => p.parcel?.is_preferred).length;
    const qualifies = pins.filter((p) => p.parcel?.qualifies).length;
    const totalSq = pins.reduce((acc, p) => acc + (Number(p.parcel?.estimated_squares) || 0), 0);
    return { preferred, qualifies, totalSq };
  }, [pins]);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60" onClick={onClose} />
      <div className="fixed inset-x-2 bottom-2 top-2 z-[60] flex flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-[min(94vw,640px)] sm:-translate-x-1/2 sm:-translate-y-1/2">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-orange-300">Drop session complete</div>
            <div className="mt-0.5 text-base font-bold">{pinCount} pin{pinCount === 1 ? "" : "s"} dropped</div>
            {typeof savedNow === "number" ? (
              <div className="mt-0.5 text-sm font-semibold text-emerald-300">
                ✓ Saved {savedNow} pin{savedNow === 1 ? "" : "s"}
                {skipped ? <span className="ml-1 font-normal text-amber-300">· {skipped} skipped (already had a pin)</span> : null}
              </div>
            ) : null}
            {dedupeUnavailable ? (
              <div className="mt-0.5 text-[11px] text-amber-300">
                ⚠ Couldn't check for existing pins this run — a few of these may double up on houses that were already pinned.
              </div>
            ) : null}
            <div className="mt-1 text-xs text-slate-300">
              <strong className="text-orange-300">{totals.preferred}</strong> preferred ·
              <strong className="ml-1 text-emerald-300">{totals.qualifies}</strong> qualifies ·
              <strong className="ml-1 text-white">~{Math.round(totals.totalSq).toLocaleString()}</strong> total squares
            </div>
          </div>
          <button onClick={onClose} className="text-2xl leading-none text-slate-300 hover:text-white" aria-label="Close">×</button>
        </div>

        {/* Sort bar */}
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs">
          <span className="text-slate-500">Sort:</span>
          {([
            ["preferred", "⭐ Preferred"],
            ["squares",   "Squares"],
            ["year",      "Year built"],
            ["address",   "Address"],
            ["newest",    "Newest"],
          ] as Array<[SortKey, string]>).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSort(k)}
              className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                sort === k ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-200"
              }`}
            >{label}</button>
          ))}
        </div>

        {/* Pin list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">Loading pins…</div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No pins in this session. {pinCount > 0 ? "(Sync may still be catching up — try Refresh.)" : ""}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visible.map((p) => {
                const homeowner = titleCaseOwner(p.homeowner_name) ?? p.homeowner_name;
                const addr = [p.property_street, p.property_city].filter(Boolean).join(", ");
                const yr = p.parcel?.year_built;
                const sq = p.parcel?.estimated_squares;
                const sale = p.parcel?.last_sale_date;
                const price = p.parcel?.last_sale_price;
                return (
                  <div
                    key={p.id}
                    className={`flex items-stretch gap-1 px-2 py-2 hover:bg-slate-50 ${deletingId === p.id ? "opacity-50" : ""}`}
                  >
                    <button
                      onClick={() => { onJumpToPin({ id: p.id, lat: Number(p.lat), lon: Number(p.lon) }); onClose(); }}
                      disabled={deletingId === p.id}
                      className="flex-1 px-2 py-0.5 text-left disabled:cursor-not-allowed"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-semibold text-slate-900">{homeowner ?? "Unknown homeowner"}</span>
                          {p.parcel?.is_preferred ? (
                            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-800">⭐ Preferred</span>
                          ) : p.parcel?.qualifies ? (
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">✓ Qualifies</span>
                          ) : null}
                        </div>
                        <span className="text-orange-500">→</span>
                      </div>
                      <div className="mt-0.5 text-xs text-slate-600">{addr}</div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                        {yr ? <span>Built <strong className="text-slate-700">{yr}</strong></span> : null}
                        {sq ? <span><strong className="text-slate-700">~{sq}</strong> sq</span> : null}
                        {sale ? <span>Sold <strong className="text-slate-700">{sale}</strong>{price ? ` · $${Number(price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}</span> : null}
                      </div>
                    </button>
                    <button
                      onClick={(e) => handleDelete(p, e)}
                      disabled={deletingId === p.id}
                      title="Delete pin"
                      aria-label={`Delete pin at ${p.property_street ?? "this location"}`}
                      className="flex w-9 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed"
                    >🗑</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-right">
          <button
            onClick={onClose}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
          >Done</button>
        </div>
      </div>
    </>
  );
}
